import { RestError } from '@azure/core-http';
import { GroupDto, KnownGroupType } from '@groundfloor-platform/mgmt-service-g-sdk';
import { useQuery } from 'react-query';
import { useApi } from '../../core/api/api.context';
import { QueryKeys } from '../../core/queries/query-keys.constants';
import { fetchAllPages } from '../../core/queries/query-helpers';

export const useQueryListGroups = (
  projectID?: string,
  excludeTopLevelGroups: boolean = false, // Enable this if you want to exclude top level groups (groups that are directly under a project)
  includeChildProjects: boolean = false, // Enable this if you want to include groups of child projects
  includeChildGroups: boolean = true, // Enable this if you want to include child groups of groups
  groupTypes?: KnownGroupType[], // Restrict to specific group types, e.g. carriers
  options?: { enabled?: boolean }
) => {
  const api = useApi();

  // The query key is made up of the constant and the optional params
  const queryKey = [QueryKeys.GROUPS];
  if (projectID) {
    queryKey.push(projectID);
  }
  if (groupTypes) {
    queryKey.push(...groupTypes);
  }

  return useQuery(
    queryKey,
    async () => {
      const items = await fetchAllPages(async (pageNo) => {
        const result = await api.groupsController.list({
          projectID,
          pageNo: pageNo,
          perPageRecords: 200,
          includeChildProjects: includeChildProjects,
          includeChildGroups: includeChildGroups,
          includeChildCounts: true
          // groupTypes is deliberately not forwarded here: the API rejects it with
          // "groupTypes must be an array" even though the SDK serializes it identically to
          // userTypes, which the API accepts — a backend validation bug on this endpoint.
          // Filtered client-side below instead.
        });
        // Filter out top level groups if needed
        if (result.items && excludeTopLevelGroups) {
          // A top level group has its parentID set to the projectID
          result.items = result.items.filter((group) => group.parentID !== projectID);
        }
        return result;
      });
      return groupTypes ? items.filter((group) => groupTypes.includes(group.groupType as KnownGroupType)) : items;
    },
    {
      // Disabled until a projectID is provided
      enabled: !!projectID && options?.enabled !== false
    }
  );
};

export function useQueryGetGroup(groupID?: string) {
  const api = useApi();

  return useQuery<GroupDto | undefined, RestError>(
    [QueryKeys.GROUPS, groupID],
    async () => {
      if (!groupID) {
        return undefined;
      }
      return await api.groupsController.get(groupID);
    },
    { enabled: !!groupID }
  );
}
